import { OmniElement, OmniStyle, OmniIcon, html, css, classMap } from 'omni-ui';

OmniStyle.register();
OmniIcon.register();

export const noblank = str =>
  [undefined, null, ''].includes(str) ? html`&nbsp;` : str;

/**
 * Handles rendering a list of items that can be selected. This is the core functionality
 * used by both the dropdown and two-pane-select classes.
 *
 * @extends OmniElement
 */
export class Om2OptionsList extends OmniElement {
  constructor() {
    super();
    this.hasObjectValues = false;
    this.isMulti = false;
    this.options = [];
    this.value = undefined;
    this.idProperty = 'value';
    this.nameProperty = 'label';
    this.selected = undefined;
    this.childrenProperty = 'children';
    this.renderOption = (option, onlyText) =>
      onlyText ? option[this.nameProperty] : noblank(option[this.nameProperty]);
  }

  static get properties() {
    return {
      /* Whether the value property should contain the whole object as appears
       * in options or just the value property
       */
      hasObjectValues: { type: Boolean },

      /* Allow choosing multiple values at once */
      isMulti: { type: Boolean },

      /* This is the full list of items to show in the dropdown */
      options: { type: Array },

      /* Instead of this.options can pass a hierarchical list of options */
      optionsTree: { type: Array },

      /* The selected value(s), either an Array for isMulti or a single value.
       * If this property is assigned to, it should align with hasObjectValues.
       * Can't be initialized with a string attribute, set the property instead.
       * Both null and undefined represent a dropdown with no selection.
       */
      value: {
        type: Array,
        hasChanged(newVal, oldVal) {
          // A poor man's deep-equal() function that considers setting a clone of
          // the same object or array as unchanged; avoids extra "change" events.
          if (
            typeof newVal === 'object' &&
            typeof oldVal === 'object' &&
            newVal !== null &&
            oldVal !== null
          )
            return JSON.stringify(newVal) !== JSON.stringify(oldVal);
          if (Number.isNaN(newVal) && Number.isNaN(oldVal)) return false;
          return newVal !== oldVal;
        },
      },

      /* Only needed in rare cases where a value is provided that is not in options.
       * In this case you can specify the label to show for value, otherwise the value
       * itself is used as the label as well.
       */
      valueLabel: { type: String },

      /* The property name to use as a unique id (default "value") */
      idProperty: { type: String },

      /* The property name to use as a display label (default "label") */
      nameProperty: { type: String },

      /* The property name to get an option's children (default "children"). Used in
       * concert with optionsTree. */
      childrenProperty: { type: String },

      /* Optional sort order of displayed options, example:
       * .sort=${(a,b) => {
       *   if (a.label > b.label) return 1;
       *   if (a.label < b.label) return -1;
       *   return 0;
       * }}
       */
      sort: { type: Function },

      /* Allow customizing how options are rendered. Can return a lit template or a
       * plain string. Takes a second argument which specifies if the return values
       * needs to be a string (e.g. for tooltip)
       */
      renderOption: { type: Function },

      /* Below are for internal use */

      /* Same as value but always an array and always stores objects */
      selected: { attribute: false },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host([autowidth]) .omni .dropdown-item:not(.is-search) {
          padding-right: 32px;
        }
        .omni .dropdown-item {
          user-select: none;
          font-size: 14px;
          line-height: 22px;
          padding: 7px 8px;
          overflow-wrap: anywhere;
        }
        .dropdown-item:hover {
          background-color: rgb(var(--rgb-core-medium-two));
        }
        .dropdown-item.is-active {
          color: rgb(var(--rgb-core-dark));
        }
        .dropdown-item.is-disabled {
          pointer-events: none;
          opacity: 0.6;
        }
        .dropdown-item:focus {
          outline: none;
          background-color: rgba(var(--rgb-primary), 0.1);
        }
        .dropdown-item.is-header {
          font-weight: 600;
        }
        .dropdown-item.unselectable {
          padding-left: 15px;
        }

        /* Single */
        .is-single .dropdown-item omni-icon[icon-id='omni:informative:check'] {
          fill: rgb(var(--rgb-primary));
          vertical-align: text-bottom;
          display: inline-block;
          width: 16px;
          height: 16px;
          margin: 3px 8px 0 0;
        }
        .is-single .dropdown-item.is-header {
          opacity: 0.4;
        }

        /* Multi */
        .is-multi .dropdown-item omni-icon[icon-id='omni:informative:check'] {
          background-color: rgb(var(--rgb-core-light));
          border: 1px solid rgb(var(--rgb-core-dark));
          fill: rgb(var(--rgb-core-light));
          border-radius: 3px;
          width: 12px;
          height: 12px;
          margin: 5px 10px 2px 2px;
        }
        .is-multi .dropdown-item omni-icon.is-implicit,
        .is-multi
          .dropdown-item.is-active
          omni-icon[icon-id='omni:informative:check'] {
          background-color: rgb(var(--rgb-primary));
          border: none;
        }
        .is-multi .dropdown-item omni-icon.is-partial {
          background-color: rgb(var(--rgb-core-medium));
          fill: rgb(var(--rgb-core-medium));
          border: none;
        }
        .is-multi .dropdown-item omni-icon.is-implicit {
          opacity: 0.5;
        }
      `,
    ];
  }

  static register(name = 'om2-options-list') {
    return super.register(name, Om2OptionsList);
  }

  initSelected() {
    // Whether this.value is an array or not we make sure that asArray always points to an array.
    let asArray = Array.isArray(this.value) ? this.value : [this.value];
    // allows user to go from a multi-select to single-select
    if (!this.isMulti && asArray.length > 1) {
      asArray = [asArray[0]];
      this.sendChangeEvent = true;
    }
    // Make sure there are no undefined/null/empty-string values
    asArray = asArray.filter(i => i !== null && i !== undefined && i !== '');

    if (
      this.hasObjectValues &&
      (asArray.length === 0 || typeof asArray[0] === 'object')
    ) {
      this.selected = asArray;
    } else {
      const optionsMap = new Map();
      this.options.forEach?.(option => {
        optionsMap.set(option[this.idProperty], option);
      });
      this.selected = asArray.map(
        value =>
          optionsMap.get(value) ??
          Object.fromEntries([
            [this.idProperty, value],
            [this.nameProperty, this.valueLabel ?? value],
          ])
      );
      if (
        this.hasObjectValues &&
        asArray.length > 0 &&
        typeof asArray[0] !== 'object'
      ) {
        this.sendChangeEvent = true;
      }
    }
  }

  shouldUpdate(changed) {
    if (
      (changed.has('optionsTree') || changed.has('sort')) &&
      this.optionsTree &&
      this.sort
    ) {
      // Sort optionsTree
      const sortChildren = children => {
        const results = children.sort(this.sort);
        results.forEach(result => {
          if (result[this.childrenProperty]) {
            // eslint-disable-next-line no-param-reassign
            result[this.childrenProperty] = sortChildren(
              result[this.childrenProperty]
            );
          }
        });
        return results;
      };
      this.optionsTree = sortChildren(this.optionsTree);
    }

    if (
      changed.has('options') &&
      this.options?.length > 0 &&
      !(this.options[0] instanceof Object)
    ) {
      // If given a list of values, transform it to a list of objects as we expect
      this.options = this.options.map(o =>
        Object.fromEntries([
          [this.idProperty, o],
          [this.nameProperty, o],
        ])
      );
    }

    if (
      (changed.has('options') || changed.has('sort')) &&
      !this.optionsTree &&
      this.options &&
      this.sort
    ) {
      // sort options (when optionsTree not defined)
      this.options = this.options.sort(this.sort);
    }

    if (changed.has('optionsTree') && this.optionsTree) {
      // Flatten optionsTree and save to this.options with metadata we can access later
      this.options = [];
      this.optionsMetadata = new Map();
      const traverse = (options, level = 0, parent = null) => {
        options.forEach(option => {
          const children = option[this.childrenProperty] ?? [];
          this.optionsMetadata.set(option[this.idProperty], {
            index: this.options.length,
            parent,
            level,
            option,
          });
          this.options.push(option);
          traverse(children, level + 1, option);
        });
      };
      traverse(this.optionsTree);
    }

    if (
      changed.has('options') &&
      this.options?.length > 0 &&
      !this.optionsTree
    ) {
      // Define the metadata (index) for options. This helps with sorting.
      this.optionsMetadata = new Map();
      this.options.forEach((option, index) => {
        this.optionsMetadata.set(option[this.idProperty], {
          index,
          level: 0,
          option,
        });
      });
    }

    if (
      !this.selected ||
      (changed.has('value') && this.value !== this.internal_value)
    ) {
      // Set internal state if we haven't already *OR* if value has been set externally
      this.initSelected();
    }

    // Transform this.selected to the correct form in this.value
    if (changed.has('selected')) {
      if (!this.isMulti && this.selected.length === 0) this.value = undefined;
      else if (this.hasObjectValues) {
        this.value = this.stripChildren(
          this.isMulti ? this.selected : this.selected[0],
          this.isMulti
        );
      } else {
        this.value = this.isMulti
          ? this.selected.map(option => option[this.idProperty])
          : (this.selected[0] || {})[this.idProperty];
      }
    }

    if (changed.has('value')) {
      // Send change event if value changed (unless we're still initializing).
      if (this.initialized || this.sendChangeEvent) {
        this.sendChangeEvent = false;
        const beforeDispatch = this.value;
        const eventName = this.initialized ? 'change' : 'init-change';
        this.dispatchNewEvent(eventName, { detail: { value: this.value } });
        // Handle edge-condition where the event callback function changed this.value
        if (this.value !== beforeDispatch) this.initSelected();
      }
      this.internal_value = this.value; // Allows us to detect external changes to this.value
    }
    return true;
  }

  firstUpdated() {
    this.initialized = true;
  }

  optionLevel(option) {
    return this.optionsMetadata?.get(option?.[this.idProperty])?.level ?? 0;
  }

  optionSortIndex(option) {
    return this.optionsMetadata?.get(option?.[this.idProperty])?.index ?? 1e10;
  }

  optionParent(option) {
    return this.optionsMetadata?.get(option?.[this.idProperty])?.parent;
  }

  optionChildren(option, noChildren = []) {
    return (
      this.optionsMetadata?.get(option?.[this.idProperty])?.option?.[
        this.childrenProperty
      ] ?? noChildren
    );
  }

  matchSort(options) {
    return options.sort(
      (a, b) => this.optionSortIndex(a) - this.optionSortIndex(b)
    );
  }

  stripChildren(options, isArray) {
    if (!this.optionsTree) return options; // Do nothing if not enabled for tree-view
    const stripChild = option => {
      const copy = { ...option };
      delete copy[this.childrenProperty];
      return copy;
    };
    return isArray ? options.map(stripChild) : stripChild(options);
  }

  // eslint-disable-next-line class-methods-use-this
  canAddOption(option) {
    return option.selectable !== false;
  }

  removeOption(e, option, i) {
    const target = e?.target || e?.srcElement;
    target?.blur();
    const index =
      i ??
      this.selected.findIndex(
        selected => selected[this.idProperty] === option[this.idProperty]
      );
    if (index < 0) return; // Nothing to do

    if (this.isMulti) {
      this.selected = [
        ...this.selected.slice(0, index),
        ...this.selected.slice(index + 1),
      ];
    } else {
      this.selected = [];
    }
  }

  toggleOption(option, index) {
    if (!this.canAddOption(option)) return; // Ignore if the option can't be added

    // Change this.selected by setting to new array to trigger updates

    if (index >= 0) {
      // Remove if multi (otherwise do nothing)
      if (this.isMulti) {
        this.removeOption(null, option, index);
      }
    } else if (this.isMulti) {
      // Check if this option is "implicitly" selected (and then unselect it)
      let implicit = false;
      const descendants = [];
      const optionParent = this.optionParent(option);
      for (let node = optionParent; node; node = this.optionParent(node)) {
        descendants.push(node);
        const found = this.selected.findIndex(
          x => x[this.idProperty] === node[this.idProperty]
        );
        if (found >= 0) {
          // Found a selected ancestor which is implicitly selecting "option"
          // Need to unselect this ancestor and select all of its descendant leaf
          // nodes (except for "option").
          implicit = true;
          this.removeOption(null, node, found);
          const nodesToAdd = [];
          descendants.forEach(descendant => {
            const children = this.optionChildren(descendant);
            children.forEach(child => {
              if (
                child !== option &&
                !descendants.includes(child) &&
                this.canAddOption(child)
              ) {
                nodesToAdd.push(child);
              }
            });
          });
          const unsorted = [...this.selected, ...nodesToAdd];
          this.selected = this.sort ? this.matchSort(unsorted) : unsorted;
          break;
        }
      }

      if (!implicit) {
        // Explicit add in multi select mode
        const unsorted = [...this.selected, option];
        this.selected = this.sort ? this.matchSort(unsorted) : unsorted;

        // Remove any child selections (they become "implicit")
        const unselectAllChilden = parent => {
          this.optionChildren(parent).forEach(child => {
            this.removeOption(null, child);
            unselectAllChilden(child);
          });
        };
        unselectAllChilden(option);

        // If all siblings checked unselect them (& self); select parent & recurse
        const siblings = this.optionChildren(optionParent);
        if (
          optionParent &&
          this.canAddOption(optionParent) &&
          siblings.every(sibling =>
            this.selected.find(
              x => x[this.idProperty] === sibling[this.idProperty]
            )
          )
        ) {
          this.toggleOption(optionParent); // recurse
          return; // Return early since the recursion will call toggleOptionComplete
        }
      }
    } else {
      // Add in single select mode
      this.selected = [option];
    }

    if (this.toggleOptionComplete) this.toggleOptionComplete(option, index);
  }

  renderPlaceholder(placeholder) {
    return typeof placeholder === 'function' ? placeholder(this) : placeholder;
  }

  renderOptionsList(filtered, { flat } = {}) {
    let toRender = filtered;

    const getDescendants = (parent, result = []) => {
      const children = this.optionChildren(parent);
      children.forEach(child => {
        result.push(child);
        getDescendants(child, result);
      });
      return result;
    };

    const getAncestors = (child, result = []) => {
      for (
        let node = this.optionParent(child);
        node;
        node = this.optionParent(node)
      ) {
        result.push(node);
      }
      return result;
    };

    const implicitOptions = new Map();
    const partialOptions = new Map();
    if (!flat) {
      this.selected.forEach(selected => {
        // all descendats are "implicitly checked"
        getDescendants(selected).forEach(descendant =>
          implicitOptions.set(descendant, true)
        );
        // all ancestors are (at least) "partially checked"
        getAncestors(selected).forEach(ancestor =>
          partialOptions.set(ancestor, true)
        );
      });

      if (toRender.length < this.options.length) {
        // Add missing ancestors if needed (can't show just the child nodes without
        // their parents).
        const set = new Set(toRender);
        toRender.forEach(option => {
          getAncestors(option).forEach(ancestor => set.add(ancestor));
        });
        if (set.size > toRender.length) {
          toRender = this.matchSort(Array.from(set));
        }
      }
    }

    const selectedIndices = Object.fromEntries(
      this.selected.map((opt, i) => [opt[this.idProperty], i])
    );

    return html`
      <div class="${this.isMulti ? 'is-multi' : 'is-single'}">
        ${toRender.map(option => {
          const selectedIndex = selectedIndices[option[this.idProperty]] ?? -1;
          const isPartial = !!partialOptions.get(option);
          const isImplicit = !!implicitOptions.get(option);
          const level = flat ? 0 : this.optionLevel(option);
          const hasChildren = this.optionChildren(option, null) !== null;

          return html`<div
            tabindex="0"
            class="dropdown-item is-clickable is-flex ${classMap({
              'is-active': selectedIndex >= 0,
              'is-disabled': option.disabled,
              'is-header': !flat && this.optionsTree && hasChildren,
              unselectable: !this.canAddOption(option),
            })}"
            title="${this.renderOption(option, true)}"
            style="margin-left: ${level}rem"
            @click=${() => this.toggleOption(option, selectedIndex)}
            @keyup=${e =>
              e.key === 'Enter' && this.toggleOption(option, selectedIndex)}
          >
            <omni-icon
              class="is-flex-shrink-0 ${classMap({
                'is-hidden': !this.canAddOption(option),
                'is-invisible': selectedIndex < 0 && !this.isMulti,
                'is-partial': selectedIndex < 0 && isPartial,
                'is-implicit': selectedIndex < 0 && isImplicit,
              })}"
              icon-id="omni:informative:check"
            ></omni-icon>
            ${noblank(this.renderOption(option))}
          </div>`;
        })}
      </div>
    `;
  }

  render() {
    return this.renderOptionsList(this.selected);
  }
}

Om2OptionsList.register();
